import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import {
  ActivityLogFilterType,
  ActivityLogProps,
} from "../../types/activityLog";

export interface ActivityLogsState {
  records: ActivityLogProps[];
  totalRecords: number;
  page: number;
  size: number;
  filter: ActivityLogFilterType;
  selectedLog: ActivityLogProps | null;
  isDetailModalOpen: boolean;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const defaultFilter: ActivityLogFilterType = {
  search: null,
  model_type: null,
  user_id: null,
  event: null,
  fromDate: null,
  toDate: null,
};

const initialState: ActivityLogsState = {
  records: [],
  totalRecords: 0,
  page: 1,
  size: 20,
  filter: { ...defaultFilter },
  selectedLog: null,
  isDetailModalOpen: false,
  loading: false,
  detailLoading: false,
  error: null,
};

type FetchListProps = {
  page?: number;
  size?: number;
  filter?: Partial<ActivityLogFilterType>;
};

export const fetchActivityLogsListData = createAsyncThunk(
  "fetchActivityLogsListData",
  async (req: FetchListProps) => {
    const params: Record<string, any> = {
      page: req.page ?? 1,
      size: req.size ?? 20,
    };
    if (req.filter) {
      Object.entries(req.filter).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          params[key] = value;
        }
      });
    }
    let response: any;
    await GET(`/v1/activity-logs`, params)
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchActivityLogById = createAsyncThunk(
  "fetchActivityLogById",
  async (id: number) => {
    let response: any;
    await GET(`/v1/activity-logs/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const activityLogsSlice = createSlice({
  name: "activityLogs",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<ActivityLogFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
      state.page = 1;
    },
    resetFilter: (state) => {
      state.filter = { ...defaultFilter };
      state.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
      state.page = 1;
    },
    openDetailModal: (state, action: PayloadAction<ActivityLogProps | null>) => {
      state.selectedLog = action.payload;
      state.isDetailModalOpen = true;
    },
    closeDetailModal: (state) => {
      state.isDetailModalOpen = false;
      state.selectedLog = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActivityLogsListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchActivityLogsListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.records = action.payload.data.data.records ?? [];
        state.totalRecords = action.payload.data.data.total_records ?? 0;
        state.error = null;
      } else {
        state.records = [];
        state.totalRecords = 0;
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchActivityLogsListData.rejected, (state, action: any) => {
      state.loading = false;
      state.records = [];
      state.totalRecords = 0;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });

    builder.addCase(fetchActivityLogById.pending, (state) => {
      state.detailLoading = true;
    });
    builder.addCase(fetchActivityLogById.fulfilled, (state, action: any) => {
      state.detailLoading = false;
      if (action.payload?.data?.status === "success") {
        state.selectedLog = action.payload.data.data ?? null;
        state.isDetailModalOpen = true;
      }
    });
    builder.addCase(fetchActivityLogById.rejected, (state) => {
      state.detailLoading = false;
    });
  },
});

export const {
  setFilter,
  resetFilter,
  setPage,
  setSize,
  openDetailModal,
  closeDetailModal,
} = activityLogsSlice.actions;

export const getActivityLogs = (state: RootState) => state.activityLogs.records;
export const getActivityLogsTotalRecords = (state: RootState) =>
  state.activityLogs.totalRecords;
export const getActivityLogsPage = (state: RootState) => state.activityLogs.page;
export const getActivityLogsSize = (state: RootState) => state.activityLogs.size;
export const getActivityLogsFilter = (state: RootState) => state.activityLogs.filter;
export const getActivityLogsLoading = (state: RootState) =>
  state.activityLogs.loading;
export const getActivityLogsError = (state: RootState) => state.activityLogs.error;
export const getSelectedActivityLog = (state: RootState) =>
  state.activityLogs.selectedLog;
export const getIsActivityLogDetailOpen = (state: RootState) =>
  state.activityLogs.isDetailModalOpen;

export default activityLogsSlice.reducer;
